import { useState } from "react";
import { MessageCircleQuestion, X, ChevronDown } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

// WhatsApp Icon Component
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Comprehensive FAQ data - hardcoded to ensure it always shows
const FAQ_DATA = [
  // Business Operations
  {
    id: "1",
    category: "Business",
    question: "What is FonderX and what services do you offer?",
    answer: "FonderX Currency Exchange Inc. is a FINTRAC-registered Money Services Business (MSB) located in New Westminster, BC. We offer fiat currency exchange (USD, EUR, GBP, CAD, INR, PHP, and 20+ more), cryptocurrency buying and selling (Bitcoin, Ethereum, USDT, and more), wire transfers, and business currency solutions."
  },
  {
    id: "2",
    category: "Business",
    question: "What are your business hours and location?",
    answer: "We are open Monday to Friday from 9:30 AM to 6:00 PM. We are closed on Saturdays, Sundays and statutory holidays. Our office is located at 2-723 12th Street, New Westminster, BC. No appointment is needed for standard transactions."
  },
  {
    id: "3",
    category: "Business",
    question: "Do I need an appointment to exchange currency?",
    answer: "No appointment is needed for standard currency exchanges. However, for large transactions exceeding $10,000 CAD, we recommend calling ahead at +1 (778) 759-0400 to ensure we have sufficient funds available and to potentially negotiate better rates."
  },
  {
    id: "4",
    category: "Business",
    question: "What identification do I need to bring?",
    answer: "For all transactions, you need a valid government-issued photo ID (passport, driver's license, or BC Services Card). For transactions over $3,000 CAD, we may require two pieces of ID. For transactions over $10,000 CAD, additional documentation about the source of funds may be required as per FINTRAC regulations."
  },
  // Fiat Currency Exchange
  {
    id: "5",
    category: "Fiat Currency",
    question: "How does fiat currency exchange work at FonderX?",
    answer: "Simply visit our office with your currency and valid ID. We'll show you our current exchange rate, which includes a small spread. Once you agree to the rate, we complete the exchange instantly - you receive your target currency in cash immediately. The entire process takes just a few minutes."
  },
  {
    id: "6",
    category: "Fiat Currency",
    question: "Which fiat currencies do you exchange?",
    answer: "We exchange 25+ fiat currencies including: USD (US Dollar), EUR (Euro), GBP (British Pound), INR (Indian Rupee), PHP (Philippine Peso), CNY (Chinese Yuan), JPY (Japanese Yen), AUD (Australian Dollar), MXN (Mexican Peso), and many more. Contact us for specific currency availability."
  },
  {
    id: "7",
    category: "Fiat Currency",
    question: "How are your exchange rates determined?",
    answer: "Our rates are based on real-time interbank market rates with a competitive spread. We update our rates throughout the day to reflect market conditions. For large transactions ($5,000+), we may offer preferential rates. Our rates are typically better than banks and airport exchanges."
  },
  {
    id: "8",
    category: "Fiat Currency",
    question: "Are there any fees for currency exchange?",
    answer: "We do not charge separate transaction fees for standard currency exchanges - our rate includes everything. What you see is what you get. For wire transfers, separate fees apply based on the destination country and amount."
  },
  // Cryptocurrency
  {
    id: "9",
    category: "Cryptocurrency",
    question: "How does cryptocurrency exchange work at FonderX?",
    answer: "For buying crypto: Bring cash and your ID to our office. We'll quote you a price (valid for ~60 seconds due to volatility), and once confirmed, we send the crypto to your wallet address. For selling crypto: Send crypto to our provided wallet address, and once confirmed on the blockchain, we pay you in cash."
  },
  {
    id: "10",
    category: "Cryptocurrency",
    question: "Which cryptocurrencies do you buy and sell?",
    answer: "We support 25+ cryptocurrencies including: Bitcoin (BTC), Ethereum (ETH), Tether (USDT), XRP, Solana (SOL), Cardano (ADA), Dogecoin (DOGE), Binance Coin (BNB), Litecoin (LTC), and more. Contact us for specific cryptocurrency availability."
  },
  {
    id: "11",
    category: "Cryptocurrency",
    question: "What do I need to buy cryptocurrency?",
    answer: "To buy cryptocurrency, you need: 1) Valid government-issued photo ID, 2) Cash (CAD) for the purchase amount, 3) Your own cryptocurrency wallet address where you want to receive the crypto. We do not provide custody services - you must have your own wallet."
  },
  {
    id: "12",
    category: "Cryptocurrency",
    question: "How long does a crypto transaction take?",
    answer: "Buying crypto: Once payment is confirmed, we initiate the transfer immediately. Bitcoin transactions typically confirm in 10-60 minutes depending on network congestion. Ethereum and other coins are usually faster. Selling crypto: We pay you once we receive sufficient blockchain confirmations (typically 1-6 depending on the coin)."
  },
  {
    id: "13",
    category: "Cryptocurrency",
    question: "Why do crypto prices change so quickly?",
    answer: "Cryptocurrency prices are determined by global supply and demand on exchanges worldwide. Prices can change by the second. That's why our quotes are only valid for approximately 60 seconds. We recommend having your wallet ready before starting a transaction to lock in your rate quickly."
  },
  // Security & Compliance
  {
    id: "14",
    category: "Security",
    question: "Is FonderX a legitimate and regulated business?",
    answer: "Yes, FonderX Currency Exchange Inc. is a fully registered Money Services Business (MSB) with FINTRAC (Financial Transactions and Reports Analysis Centre of Canada). We comply with all Canadian anti-money laundering (AML) and Know Your Customer (KYC) regulations. Your transactions are secure and legally compliant."
  },
  {
    id: "15",
    category: "Security",
    question: "How do you protect my personal information?",
    answer: "We take privacy seriously. Your personal information is collected only as required by law and is protected using industry-standard security measures. We never sell your data to third parties. Please see our Privacy Policy for complete details on how we handle your information."
  },
  // Rates & Pricing
  {
    id: "16",
    category: "Rates",
    question: "How do your rates compare to banks?",
    answer: "Our rates are typically 1-3% better than major Canadian banks (TD, RBC, Scotiabank, BMO, CIBC). Banks often have wider spreads and may charge additional fees. You can use our Bank Comparison tool on the website to see real-time comparisons with major banks."
  },
  {
    id: "17",
    category: "Rates",
    question: "Do you offer better rates for large amounts?",
    answer: "Yes! For exchanges over $5,000 CAD, we can often offer preferential rates. For very large transactions ($25,000+), please call us in advance to discuss rates and ensure availability. Business accounts may also qualify for special pricing."
  }
];

// Accordion Item Component
function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div className="border border-white/10 rounded-lg overflow-hidden bg-white/5 mb-2">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 text-left text-sm font-medium text-white hover:bg-white/5 flex items-center justify-between"
      >
        <span>{faq.question}</span>
        <ChevronDown className={`w-4 h-4 text-blue-100/60 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-4 pb-3 text-sm text-blue-100/70 leading-relaxed">
          {faq.answer}
        </div>
      )}
    </div>
  );
}

export default function FAQWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  // WhatsApp only. The shop's main line (778) 759-0400 stays on every
  // tel: link on the site - this is the number that answers WhatsApp.
  // wa.me needs the full international number, so 236 868 1195 is
  // written with Canada's country code in front of it.
  const whatsappNumber = "12368681195";
  const whatsappLink = `https://wa.me/${whatsappNumber}`;

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <div className="faq-widget" data-testid="faq-widget">
      {isOpen && (
        <div className="faq-panel animate-fade-in-up" data-testid="faq-panel">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <MessageCircleQuestion className="w-5 h-5 text-blue-400" />
              <h3 className="font-semibold text-white">Frequently Asked Questions</h3>
            </div>
            <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/10 rounded-lg">
              <X className="w-5 h-5 text-blue-100/60" />
            </button>
          </div>

          <ScrollArea className="h-[450px]">
            <div className="p-4">
              {/* Business Section */}
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-3">About Our Business</h4>
                {FAQ_DATA.filter(f => f.category === "Business").map((faq) => (
                  <FAQItem 
                    key={faq.id} 
                    faq={faq} 
                    isOpen={openFAQ === faq.id} 
                    onToggle={() => toggleFAQ(faq.id)} 
                  />
                ))}
              </div>

              {/* Fiat Currency Section */}
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-3">Fiat Currency Exchange</h4>
                {FAQ_DATA.filter(f => f.category === "Fiat Currency").map((faq) => (
                  <FAQItem 
                    key={faq.id} 
                    faq={faq} 
                    isOpen={openFAQ === faq.id} 
                    onToggle={() => toggleFAQ(faq.id)} 
                  />
                ))}
              </div>

              {/* Cryptocurrency Section */}
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-3">Cryptocurrency Exchange</h4>
                {FAQ_DATA.filter(f => f.category === "Cryptocurrency").map((faq) => (
                  <FAQItem 
                    key={faq.id} 
                    faq={faq} 
                    isOpen={openFAQ === faq.id} 
                    onToggle={() => toggleFAQ(faq.id)} 
                  />
                ))}
              </div>

              {/* Security Section */}
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-purple-400 uppercase tracking-wider mb-3">Security & Compliance</h4>
                {FAQ_DATA.filter(f => f.category === "Security").map((faq) => (
                  <FAQItem 
                    key={faq.id} 
                    faq={faq} 
                    isOpen={openFAQ === faq.id} 
                    onToggle={() => toggleFAQ(faq.id)} 
                  />
                ))}
              </div>

              {/* Rates Section */}
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-cyan-400 uppercase tracking-wider mb-3">Rates & Pricing</h4>
                {FAQ_DATA.filter(f => f.category === "Rates").map((faq) => (
                  <FAQItem 
                    key={faq.id} 
                    faq={faq} 
                    isOpen={openFAQ === faq.id} 
                    onToggle={() => toggleFAQ(faq.id)} 
                  />
                ))}
              </div>
            </div>
          </ScrollArea>

          <div className="p-4 border-t border-white/10">
            <p className="text-xs text-blue-100/40 text-center">
              Still have questions? <a href="/contact" className="text-blue-400 hover:underline">Contact us</a> or call <a href="tel:+17787590400" className="text-blue-400 hover:underline">+1 (778) 759-0400</a>
            </p>
          </div>
        </div>
      )}

      {/* Floating Buttons */}
      <div className="flex flex-col gap-3">
        {/* WhatsApp Button */}
        <a 
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-button"
          aria-label="Chat on WhatsApp"
          data-testid="whatsapp-button"
        >
          <WhatsAppIcon />
        </a>

        {/* FAQ Button */}
        <button onClick={() => setIsOpen(!isOpen)} className="faq-button" aria-label="Toggle FAQ">
          {isOpen ? <X className="w-6 h-6" /> : <MessageCircleQuestion className="w-6 h-6" />}
        </button>
      </div>
    </div>
  );
}
